import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, BarChart2, TrendingUp, Target } from 'lucide-react';
export function Hero() {
  return <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden bg-slate-50" data-id="element-43">
      {/* Abstract Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-44">
        <div className="absolute -top-[20%] -right-[10%] w-[600px] h-[600px] bg-blue-100/40 rounded-full blur-3xl opacity-60" data-id="element-45" />
        <div className="absolute top-[40%] -left-[10%] w-[500px] h-[500px] bg-slate-200/40 rounded-full blur-3xl opacity-60" data-id="element-46" />

        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20" data-id="element-47"></div>
        <div className="absolute inset-0" style={{
        backgroundImage: 'radial-gradient(#cbd5e1 1px, transparent 1px)',
        backgroundSize: '40px 40px',
        opacity: 0.3
      }} data-id="element-48"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-49">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20" data-id="element-50">
          {/* Text Content */}
          <motion.div className="flex-1 text-center lg:text-left" initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} data-id="element-51">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2,
            duration: 0.6
          }} className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-sm font-medium mb-6 border border-blue-100" data-id="element-52">
              <span className="flex h-2 w-2 rounded-full bg-blue-600 mr-2" data-id="element-53"></span>
              Especialistas en Performance Digital
            </motion.div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-navy-900 tracking-tight leading-[1.15] mb-6" data-id="element-54">
              Publicidad digital enfocada en{' '}
              <span className="text-blue-600" data-id="element-55">resultados reales</span>
            </h1>

            <p className="text-lg md:text-xl text-slate-600 mb-8 max-w-2xl mx-auto lg:mx-0 leading-relaxed" data-id="element-56">
              Gestión estratégica de campañas, optimización constante y control
              total de tu inversión publicitaria.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4" data-id="element-57">
              <a href="https://calendly.com/gooutperformance/30min" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-bold text-lg transition-all shadow-lg hover:shadow-blue-500/25 flex items-center justify-center group" data-id="element-58">
                Agendar reunión
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" data-id="element-59" />
              </a>
              <a href="#servicios" className="w-full sm:w-auto text-slate-700 hover:text-blue-600 px-8 py-4 rounded-lg font-semibold transition-colors flex items-center justify-center" data-id="element-60">
                Conocer servicios
              </a>
            </div>
          </motion.div>

          {/* Visual Element */}
          <motion.div className="flex-1 w-full max-w-lg lg:max-w-none" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.4,
          duration: 0.8
        }} data-id="element-61">
            <div className="relative bg-white rounded-2xl shadow-2xl border border-slate-100 p-6 md:p-8 overflow-hidden" data-id="element-62">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-blue-700" data-id="element-63"></div>

              {/* Mock Dashboard UI */}
              <div className="space-y-6" data-id="element-64">
                <div className="flex justify-between items-center mb-4" data-id="element-65">
                  <div data-id="element-66">
                    <div className="h-2 w-24 bg-slate-200 rounded mb-2" data-id="element-67"></div>
                    <div className="h-4 w-48 bg-slate-900 rounded" data-id="element-68"></div>
                  </div>
                  <div className="h-8 w-8 rounded-full bg-blue-50 flex items-center justify-center" data-id="element-69">
                    <TrendingUp className="h-4 w-4 text-blue-600" data-id="element-70" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4" data-id="element-71">
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-100" data-id="element-72">
                    <div className="flex items-center space-x-2 text-slate-500 text-sm mb-2" data-id="element-73">
                      <Target className="h-4 w-4" data-id="element-74" />
                      <span data-id="element-75">Conversiones</span>
                    </div>
                    <div className="text-2xl font-bold text-navy-900" data-id="element-76">
                      +127%
                    </div>
                    <div className="text-xs text-green-600 font-medium mt-1" data-id="element-77">
                      ↑ vs mes anterior
                    </div>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-100" data-id="element-78">
                    <div className="flex items-center space-x-2 text-slate-500 text-sm mb-2" data-id="element-79">
                      <BarChart2 className="h-4 w-4" data-id="element-80" />
                      <span data-id="element-81">ROAS</span>
                    </div>
                    <div className="text-2xl font-bold text-navy-900" data-id="element-82">4.5x</div>
                    <div className="text-xs text-green-600 font-medium mt-1" data-id="element-83">
                      ↑ Optimizado
                    </div>
                  </div>
                </div>

                <div className="space-y-3 pt-2" data-id="element-84">
                  <div className="flex justify-between text-sm" data-id="element-85">
                    <span className="text-slate-500" data-id="element-86">Campaña Q3 - Leads</span>
                    <span className="font-medium text-navy-900" data-id="element-87">Activa</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2" data-id="element-88">
                    <motion.div className="bg-blue-600 h-2 rounded-full" initial={{
                    width: '0%'
                  }} animate={{
                    width: '75%'
                  }} transition={{
                    delay: 1,
                    duration: 1.5,
                    ease: 'easeOut'
                  }} data-id="element-89" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}